import type { HTMLAttributes } from 'react'
import React, { useId } from 'react'
import { cn } from '@/utils'

interface InputProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  id?: string
  label?: string
  value?: string
  type?: string
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  required?: boolean
  loading?: boolean
  error?: boolean
  errorText?: string
  onChange?: (value: string) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  InputProps?: Omit<HTMLAttributes<HTMLInputElement>, 'id'>
  LabelProps?: HTMLAttributes<HTMLLabelElement>
}

export const Input: React.FC<InputProps> = ({
  id: propsId,
  label = '',
  value = '',
  type = 'text',
  placeholder = 'Enter text...',
  autoFocus = false,
  disabled = false,
  required = false,
  loading = false,
  error = false,
  errorText,
  onChange,
  onBlur,
  InputProps,
  LabelProps,
  ...props
}) => {
  const generatedId = useId()

  const id = propsId ?? generatedId
  const errorId = `${id}-error`

  const describedBy = [
    InputProps?.['aria-describedby'] as string | undefined,
    error ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  const mergedInputProps = {
    ...InputProps,
    'aria-invalid': error || undefined,
    'aria-describedby': describedBy || InputProps?.['aria-describedby'],
  } as HTMLAttributes<HTMLInputElement>

  return (
    <div
      className={cn(loading && 'bg-animated from-gray-900 to-gray-800')}
      {...props}
    >
      <label
        htmlFor={id}
        className="block pt-2 text-blue-500 text-xs"
        {...LabelProps}
      >
        {label}
      </label>
      <input
        id={id}
        className={cn(
          'h-9 mt-1 w-full px-3 text-white bg-gray-800 focus:outline-hidden focus:ring-3 rounded-sm',
          error && 'ring-1 ring-red-500 focus:ring-red-500',
        )}
        type={type}
        disabled={disabled}
        autoFocus={autoFocus}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        {...mergedInputProps}
      />
      {error && !!errorText && (
        <p id={errorId} className="mt-1 text-xs text-red-500">
          {errorText}
        </p>
      )}
    </div>
  )
}
